/**
 * Takeable spur branches (M13) — the branch-taking beyond diamonds the
 * hole-semantics round promised: a split siding on an ELEVATED straight
 * whose one-cell branch is an `end-hole` diving board cantilevered
 * DIRECTLY above a lower ring channel (Δ exactly 1.0 — the bridge law).
 * The spur is a GUARANTEED drain (end-hole = guaranteed fall, user rule):
 * on alternate laps the marble takes the branch, rolls out over the void,
 * drops through the hole, and continues on the lower line.
 *
 * Mechanically this composes two existing systems instead of inventing a
 * third: the split window gains a LoopSegment.alt (the diamond machinery
 * — a teardrop out over the hole and back, never ridden past the hole),
 * and the drop itself is a Track.falls entry (the bridge chance-fall
 * machinery) flagged `guaranteed`, active only on the variant that
 * actually passes the hole.
 */
import type {
  CurveSeg,
  Dir,
  FallPoint,
  LoopSegment,
  Placement,
  Vec3,
} from "../shared/types.ts";
import type { Skeleton } from "./topology.ts";
import type { ElevationPlan } from "./elevation.ts";
import type { StyleField } from "./style.ts";
import type { Rng } from "./rng.ts";
import { DIR_STEP, segLength } from "./geom.ts";
import { emitSupportStack } from "./supports.ts";

const SPUR_CHANCE = 0.6;
/** Prefer the wide bridge form when its footprint fits (it is far rarer). */
const WIDE_PREFERENCE = 0.85;

export interface TakeablePlan {
  /** New branch placements (the host straight is mutated in place). */
  placements: Placement[];
  falls: FallPoint[];
  cells: string[];
}

export function planTakeableSpurs(
  rng: Rng,
  skeleton: Skeleton,
  elevation: ElevationPlan,
  style: StyleField,
  /** All assembled placements — the chosen host mutates straight → split. */
  placements: Placement[],
  /** Loop segments (assemble output) — the host window gains `alt`. */
  segments: LoopSegment[],
  /** Full track footprint so far (2D cells) — wide decks must not clip. */
  occupied: ReadonlySet<string>,
): TakeablePlan {
  const empty: TakeablePlan = { placements: [], falls: [], cells: [] };
  if (!rng.chance(SPUR_CHANCE)) return empty;

  const key = (x: number, z: number) => `${x},${z}`;
  // Ring cell → base height; twice-visited cells are ambiguous, skip them.
  const cellHeight = new Map<string, number>();
  const visits = new Map<string, number>();
  skeleton.cells.forEach((c, i) => {
    const k = key(c.x, c.z);
    cellHeight.set(k, elevation.baseY[i]);
    visits.set(k, (visits.get(k) ?? 0) + 1);
  });
  const arched = new Set(
    placements
      .filter((p) => p.pieceId === "tunnel")
      .map((p) => key(Math.round(p.pos[0]), Math.round(p.pos[2]))),
  );
  const goalCells = new Set(
    placements
      .filter((p) => p.goal)
      .map((p) => key(Math.round(p.pos[0]), Math.round(p.pos[2]))),
  );

  interface Candidate {
    seg: LoopSegment;
    host: Placement;
    w: Dir; // branch direction (toward the hole cell)
    flow: Dir;
    hole: [number, number];
    /** Wide bridge form: funnel → straight-wide-hole → funnel → cap. */
    wide: boolean;
  }
  /** A lower-line cell suitable for the drain hole at deck height y. */
  const drainable = (k: string, y: number): boolean =>
    cellHeight.get(k) === y - 1 && !arched.has(k) && !goalCells.has(k) &&
    (visits.get(k) ?? 0) <= 1;
  /** Free air at deck height (not track, ground open for a pillar). */
  const air = (k: string): boolean =>
    !occupied.has(k) && cellHeight.get(k) === undefined;
  /** Wide-deck flank: open air, or the lower line a full course below. */
  const flankOk = (k: string, y: number): boolean =>
    air(k) || (cellHeight.get(k) !== undefined && cellHeight.get(k)! <= y - 1);
  const candidates: Candidate[] = [];
  for (const seg of segments) {
    if (seg.placementIndex < 0 || seg.alt) continue;
    const host = placements[seg.placementIndex];
    if (!host || host.pieceId !== "straight" || host.role !== "track") {
      continue;
    }
    const y = host.pos[1];
    if (y < 1) continue; // needs a full course of air below the board
    const cx = Math.round(host.pos[0]);
    const cz = Math.round(host.pos[2]);
    const hk = key(cx, cz);
    if (arched.has(hk) || goalCells.has(hk) || (visits.get(hk) ?? 0) > 1) {
      continue;
    }
    const flow = ((1 - host.rotation + 4) % 4) as Dir;
    for (const side of [1, 3] as const) {
      const w = ((flow + side) % 4) as Dir;
      const [dx, dz] = DIR_STEP[w];
      const [fx, fz] = DIR_STEP[flow];
      const at = (n: number): [number, number] => [cx + dx * n, cz + dz * n];
      // NARROW: the hole cell is the adjacent lower ring cell.
      if (drainable(key(...at(1)), y)) {
        candidates.push({ seg, host, w, flow, hole: at(1), wide: false });
      }
      // WIDE BRIDGE (M13): funnel@1 → straight-wide-hole@2 (over the
      // lower line) → reversed funnel@3 → cap@4. The deck cells need
      // open air with pillar ground; every deck cell's lateral flanks
      // (the 0.35 wide bulge) need air or the lower line a full course
      // below.
      if (
        drainable(key(...at(2)), y) &&
        air(key(...at(1))) && air(key(...at(3))) && air(key(...at(4))) &&
        [1, 2, 3].every((n) => {
          const [bx, bz] = at(n);
          return flankOk(key(bx + fx, bz + fz), y) &&
            flankOk(key(bx - fx, bz - fz), y);
        })
      ) {
        candidates.push({ seg, host, w, flow, hole: at(2), wide: true });
      }
    }
  }
  if (candidates.length === 0) return empty;

  // One spur per track — a set-piece, not a texture. Wide bridges are far
  // rarer geometry; prefer one when it fits.
  const sorted = candidates.sort((a, b) =>
    a.hole[0] - b.hole[0] || a.hole[1] - b.hole[1] || a.w - b.w ||
    Number(a.wide) - Number(b.wide)
  );
  const wides = sorted.filter((c) => c.wide);
  const cand = wides.length > 0 && rng.chance(WIDE_PREFERENCE)
    ? rng.pick(wides)
    : rng.pick(sorted);
  const { host, w, flow, hole } = cand;
  const y = host.pos[1];
  const floor = y + 0.25;
  const cx = Math.round(host.pos[0]);
  const cz = Math.round(host.pos[2]);
  const [fx, fz] = DIR_STEP[flow];
  const [wx, wz] = DIR_STEP[w];

  // Host mutates into the junction (the split's main channel IS the
  // host's channel; a right-hand branch renders the native mesh mirrored).
  host.pieceId = "split";
  if ((flow + 3) % 4 === w) host.mirrored = true;

  const rW = ((1 - w + 4) % 4) as Dir;
  const spurPlacements: Placement[] = [];
  const cells: string[] = [];
  /** Turnaround cell for the teardrop (the last deck cell ridden out). */
  let tip: [number, number];

  if (cand.wide) {
    const at = (n: number): [number, number] => [
      cx + wx * n,
      cz + wz * n,
    ];
    spurPlacements.push(
      {
        pieceId: "funnel",
        pos: [at(1)[0], y, at(1)[1]],
        rotation: rW,
        role: "track",
      },
      {
        pieceId: "straight-wide-hole",
        pos: [at(2)[0], y, at(2)[1]],
        rotation: rW,
        role: "track",
      },
      {
        pieceId: "funnel",
        pos: [at(3)[0], y, at(3)[1]],
        rotation: ((rW + 2) % 4) as Dir,
        role: "track",
      },
      {
        pieceId: rng.chance(0.5) ? "end-rounded" : "end-square",
        pos: [at(4)[0], y, at(4)[1]],
        rotation: rW,
        role: "track",
      },
    );
    // Falsework mirroring the assemble greedy: a plain column under the
    // funnel mouth, a cradle abutment under the cap.
    const supportPrefix = style.supportColor ? "support-color" : "support";
    emitSupportStack(
      spurPlacements,
      supportPrefix,
      style.supportKind,
      at(1)[0],
      at(1)[1],
      y,
      w,
      false,
      0,
      true, // loop-only falsework: M19c pattern pool admitted
    );
    emitSupportStack(
      spurPlacements,
      supportPrefix,
      style.supportKind,
      at(4)[0],
      at(4)[1],
      y,
      w,
      true,
      0,
      true,
    );
    for (let n = 1; n <= 4; n++) cells.push(key(...at(n)));
    tip = at(3);
  } else {
    spurPlacements.push({
      pieceId: rng.chance(0.5) ? "end-hole-rounded" : "end-hole-square",
      pos: [hole[0], y, hole[1]],
      rotation: rW,
      role: "track",
    });
    cells.push(key(...hole));
    tip = hole;
  }

  // The teardrop alt: entry port → junction → out over the hole to the
  // tip → back → exit port. The marble never rides the return leg — the
  // fall at the hole is guaranteed — but the variant stays a closed,
  // well-formed loop.
  const pts: Vec3[] = [
    [cx - fx / 2, floor, cz - fz / 2],
    [cx, floor, cz],
    [cx + wx * 0.35 - fx * 0.12, floor, cz + wz * 0.35 - fz * 0.12],
    ...(cand.wide
      ? [[hole[0] - fx * 0.15, floor, hole[1] - fz * 0.15] as Vec3]
      : []),
    [
      tip[0] - wx * 0.15 - fx * 0.18,
      floor,
      tip[1] - wz * 0.15 - fz * 0.18,
    ],
    [tip[0] + wx * 0.22, floor, tip[1] + wz * 0.22],
    [
      tip[0] - wx * 0.15 + fx * 0.18,
      floor,
      tip[1] - wz * 0.15 + fz * 0.18,
    ],
    ...(cand.wide
      ? [[hole[0] + fx * 0.18, floor, hole[1] + fz * 0.18] as Vec3]
      : []),
    [cx + wx * 0.35 + fx * 0.12, floor, cz + wz * 0.35 + fz * 0.12],
    [cx, floor, cz],
    [cx + fx / 2, floor, cz + fz / 2],
  ];
  const curve: CurveSeg[] = [];
  for (let i = 0; i + 1 < pts.length; i++) {
    curve.push({ kind: "line", a: pts[i], b: pts[i + 1] });
  }
  cand.seg.alt = {
    curve,
    length: curve.reduce((acc, s) => acc + segLength(s), 0),
  };

  return {
    placements: spurPlacements,
    falls: [{
      pos: [hole[0], floor, hole[1]],
      landPos: [hole[0], floor - 1, hole[1]],
      guaranteed: true,
    }],
    cells,
  };
}
